import type { SasDecodeResult, SasDecodedLine, SasExchange } from '../../api/types';
import { isErrorWarning } from './lineModel';

/**
 * One entry in the command-search dropdown: either a specific poll command
 * (keyed by its command byte), the "General poll" convenience bucket, or the
 * "Errors" convenience bucket (CRC-BAD / NACK exchanges). Built fresh from
 * the current decode result by `buildCommandSearchIndex` — pure / stateless;
 * all nav state (selection, position, scroll trigger) lives in the caller
 * (`SasAnalyzerPage`).
 */
export type SasCommandSearchOption = {
  /** Stable identity for the option — `'general-poll'`, `'errors'`, or
   * `cmd:<decimal command byte>` for a real command. Deliberately NOT
   * derived from `label` (which embeds a live occurrence count and would
   * otherwise mint a new identity every decode, breaking MUI Autocomplete's
   * `isOptionEqualToValue`). */
  key: string;
  /** Display text including the occurrence count, e.g.
   * "0x2F — Send selected meters for game n (168)". */
  label: string;
  /** Index into `result.exchanges` for every matching exchange, in stream
   * order — the same order `buildLineModel` / `buildExchangeRanges` render
   * them in. Its length is the count baked into `label`. */
  exchangeIndexes: number[];
};

// `isErrorWarning` is the SAME test lineModel.ts uses to colour a warning
// line err-role — imported (not re-implemented) so the "Errors" dropdown
// entry can never drift from what the stream itself renders as an error.
function exchangeHasError(exchange: SasExchange, lines: readonly SasDecodedLine[]): boolean {
  return exchange.lineIndexes.some((idx) => {
    const l = lines[idx];
    if (!l) return false;
    if (l.crc === 'Bad') return true;
    return l.warnings.some((w) => isErrorWarning(w));
  });
}

function hexByte(command: number): string {
  return `0x${command.toString(16).toUpperCase().padStart(2, '0')}`;
}

type ExchangeCommandKey =
  | { kind: 'generalPoll' }
  | { kind: 'command'; command: number; name: string | null };

// The line that "names" an exchange for search purposes: the first line
// carrying either a GeneralPoll pollType or a non-null command byte. Per
// SasDecoder.cs, only the line that ORIGINATED the exchange (its TX poll,
// or an unpaired frame classified as a command) carries a raw command byte
// on the wire — a paired response/ACK/NACK line's `command` is always null
// (SAS response frames don't repeat the command byte), so scanning in
// stream order and taking the first hit reliably finds the originating
// line. An exchange with neither (e.g. Busy, or a fully unparseable frame)
// yields `null` and is excluded from every command/general-poll bucket
// (it may still land in the Errors bucket via `exchangeHasError`).
function exchangeCommandKey(exchange: SasExchange, lines: readonly SasDecodedLine[]): ExchangeCommandKey | null {
  for (const idx of exchange.lineIndexes) {
    const l = lines[idx];
    if (!l) continue;
    if (l.pollType === 'GeneralPoll') return { kind: 'generalPoll' };
    if (l.command !== null) return { kind: 'command', command: l.command, name: l.commandName };
  }
  return null;
}

/**
 * Build the command-search dropdown's option list from a decode result: one
 * entry per poll command PRESENT in the decode (ordered by command byte
 * ascending), plus a "General poll" entry (first) when any general-poll
 * exchanges exist, plus an "Errors" convenience entry (last) when any
 * CRC-BAD/NACK exchange exists. An empty decode (or one where no exchange
 * carries a recognisable command/poll/error) yields `[]`.
 */
export function buildCommandSearchIndex(result: SasDecodeResult): SasCommandSearchOption[] {
  const generalPollExchanges: number[] = [];
  const commandExchanges = new Map<number, { name: string | null; exchanges: number[] }>();
  const errorExchanges: number[] = [];

  result.exchanges.forEach((exchange, exchangeIndex) => {
    const key = exchangeCommandKey(exchange, result.lines);
    if (key?.kind === 'generalPoll') {
      generalPollExchanges.push(exchangeIndex);
    } else if (key?.kind === 'command') {
      const entry = commandExchanges.get(key.command) ?? { name: null, exchanges: [] };
      if (entry.name === null && key.name !== null) entry.name = key.name;
      entry.exchanges.push(exchangeIndex);
      commandExchanges.set(key.command, entry);
    }

    if (exchangeHasError(exchange, result.lines)) errorExchanges.push(exchangeIndex);
  });

  const options: SasCommandSearchOption[] = [];

  if (generalPollExchanges.length > 0) {
    options.push({
      key: 'general-poll',
      label: `General poll (${generalPollExchanges.length})`,
      exchangeIndexes: generalPollExchanges,
    });
  }

  for (const command of Array.from(commandExchanges.keys()).sort((a, b) => a - b)) {
    const { name, exchanges } = commandExchanges.get(command)!;
    options.push({
      key: `cmd:${command}`,
      label: `${hexByte(command)} — ${name ?? 'Unregistered command'} (${exchanges.length})`,
      exchangeIndexes: exchanges,
    });
  }

  if (errorExchanges.length > 0) {
    options.push({
      key: 'errors',
      label: `Errors (CRC BAD / NACK) (${errorExchanges.length})`,
      exchangeIndexes: errorExchanges,
    });
  }

  return options;
}

/**
 * Wrap a 0-based position by one step within `[0, length)` — the entirety of
 * the command-search prev/next nav logic. `length <= 0` (no matches) always
 * returns 0; a single-element list (`length === 1`) wraps back to itself in
 * either direction.
 */
export function wrapIndex(pos: number, length: number, direction: 1 | -1): number {
  if (length <= 0) return 0;
  return (pos + direction + length) % length;
}
